import { c as _c } from "react/compiler-runtime";
import Button from '@precis-digital/kurama/Button';
import Divider from '@precis-digital/kurama/Divider';
import Grid from '@precis-digital/kurama/Grid';
import SingleSelectDropdown from '@precis-digital/kurama/SingleSelectDropdown';
import { styled } from '@precis-digital/kurama/utils';
import Typography from '@precis-digital/kurama/Typography';
import React, { useState } from 'react';
import { StyledProfileItem } from './workspaceProfileStyles';
import { type ClientError } from 'shared/api/fetch';
import InlineFlyoutAvatarUpload from 'shared/components/AvatarUpload/InlineFlyoutAvatarUpload';
import Dots from 'shared/components/Loader/Dots';
import { makeErrorToast } from 'shared/components/Toaster';
import { currencies, frequentlyUsedCurrencies } from 'shared/constants/currency';
import { type Client } from 'shared/context/ClientContext';
import { useForm, useWatch } from 'shared/reactHookForm';
import { type TFunction, useTranslation } from 'shared/translations';
import { trimWhitespace } from 'shared/utils/stringFormatter';
import { useUpdateClient } from 'src/workspace/api';
import DeleteWorkspacePopup from '../Popup/DeleteWorkspacePopup';
import WorkspaceInput from '../WorkspaceInput';
import { EditInputContainer } from './styles';
interface FormProps {
  name: string;
  url: string;
  [key: string]: string;
}
interface ProfileProps {
  currentClient: Client;
  uploadAvatarProps: {
    uploadAvatar: (file: File) => Promise<void>;
    isUploading: boolean;
    isUploadSuccess: boolean;
  };
}
const TOAST_LABEL_KEYS = {
  default: 'workspaceSetup.toasters.updateWorkspaceProfileError',
  validation: 'workspaceSetup.toasters.updateWorkspaceProfileValidationError',
  notFound: 'workspaceSetup.toasters.updateWorkspaceProfileNotFound'
} as const;
const getUpdateProfileErrorMessage = (error: ClientError, t: TFunction<'workspace'>): string => {
  if (error.status_code === 404) return t(TOAST_LABEL_KEYS.notFound);
  if (error.status_code === 400) return t(TOAST_LABEL_KEYS.validation);
  return t(TOAST_LABEL_KEYS.default);
};
const WorkspaceProfile = props => {
  const $ = _c(30);
  const {
    currentClient,
    uploadAvatarProps
  } = props;
  const {
    t
  } = useTranslation("workspace");
  const [isEditName, setIsEditName] = useState(false);
  const [isEditUrl, setIsEditUrl] = useState(false);
  const [isEditCurrency, setIsEditCurrency] = useState(false);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const {
    mutate: updateClient,
    isPending: isUpdateClientLoading
  } = useUpdateClient();
  const t0 = currentClient.url ?? "";
  let t1;
  if ($[0] !== currentClient.currency || $[1] !== currentClient.name || $[2] !== t0) {
    t1 = {
      name: currentClient.name,
      url: t0,
      currency: currentClient.currency
    };
    $[0] = currentClient.currency;
    $[1] = currentClient.name;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const DEFAULT_VALUES = t1;
  let t2;
  if ($[4] !== DEFAULT_VALUES) {
    t2 = {
      defaultValues: {
        ...DEFAULT_VALUES
      }
    };
    $[4] = DEFAULT_VALUES;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    formState: t3,
    control,
    clearErrors,
    reset,
    handleSubmit,
    setValue
  } = useForm(t2);
  const {
    isDirty,
    dirtyFields
  } = t3;
  let t4;
  if ($[6] !== control) {
    t4 = {
      control,
      name: "currency"
    };
    $[6] = control;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const watchCurrency = useWatch(t4);
  let t5;
  if ($[8] !== dirtyFields || $[9] !== isDirty || $[10] !== reset || $[11] !== t || $[12] !== updateClient) {
    t5 = async (clientId, formData) => {
      if (!isDirty) {
        return;
      }
      const updatedData = Object.keys(dirtyFields).reduce((result, dirtyField, index) => ({
        [dirtyField]: formData[dirtyField],
        ...result
      }), {});
      if (updatedData?.name != null) {
        updatedData.name = trimWhitespace(updatedData.name);
      }
      updateClient({
        id: clientId,
        ...updatedData
      }, {
        onSuccess: data => {
          reset({
            name: data.name,
            url: data.url,
            currency: data.currency
          });
        },
        onError: error => {
          makeErrorToast(getUpdateProfileErrorMessage(error, t));
        }
      });
    };
    $[8] = dirtyFields;
    $[9] = isDirty;
    $[10] = reset;
    $[11] = t;
    $[12] = updateClient;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleUpdateClient = t5;
  let t6;
  if ($[14] !== clearErrors || $[15] !== control || $[16] !== currentClient || $[17] !== handleSubmit || $[18] !== handleUpdateClient || $[19] !== isEditCurrency || $[20] !== isEditName || $[21] !== isEditUrl || $[22] !== isUpdateClientLoading || $[23] !== openDeletePopup || $[24] !== reset || $[25] !== setValue || $[26] !== t || $[27] !== uploadAvatarProps || $[28] !== watchCurrency) {
    t6 = <Grid>{isUpdateClientLoading ? <Dots /> : <><StyledTitle variant="h3">{t("titles.workspaceProfile")}</StyledTitle><StyledDescription variant="body1">{t("descriptions.workspaceProfile")}</StyledDescription><StyledProfileItem>{isEditName ? <Grid><Typography variant="h5">{t("titles.companyName")}</Typography><Typography variant="body3">{t("descriptions.companyName")}</Typography><EditInputsContainer><EditInputContainer><WorkspaceInput name="name" control={control} onKeyDown={e => {
                  if (e.key === "Enter") {
                    handleSubmit(async updatedClient => {
                      await handleUpdateClient(currentClient.id, updatedClient);
                      setIsEditName(false);
                    })();
                  }
                }} /></EditInputContainer><Button onClick={() => {
                handleSubmit(async updatedClient_0 => {
                  await handleUpdateClient(currentClient.id, updatedClient_0);
                  setIsEditName(false);
                })();
              }} scheme="light" variant="filled">{t("buttons.save")}</Button><Button onClick={() => {
                reset({
                  name: currentClient.name,
                  url: currentClient.url,
                  currency: currentClient.currency
                });
                setIsEditName(false);
                clearErrors("name");
              }} scheme="light" variant="tonal">{t("buttons.cancel")}</Button></EditInputsContainer></Grid> : <><Grid><Typography variant="body2">{t("placeholders.companyName")}</Typography><Typography variant="body3">{currentClient.name}</Typography></Grid><Button variant="text" onClick={() => {
              setIsEditName(true);
            }}>{t("buttons.change")}</Button></>}</StyledProfileItem><Divider /><StyledProfileItem>{isEditUrl ? <Grid><Typography variant="h5">{t("titles.website")}</Typography><Typography variant="body3">{t("descriptions.website")}</Typography><EditInputsContainer><EditInputContainer><WorkspaceInput name="url" control={control} onKeyDown={e_0 => {
                  if (e_0.key === "Enter") {
                    handleSubmit(async updatedClient_1 => {
                      await handleUpdateClient(currentClient.id, updatedClient_1);
                      setIsEditUrl(false);
                    })();
                  }
                }} /></EditInputContainer><Button onClick={() => {
                handleSubmit(async updatedClient_2 => {
                  await handleUpdateClient(currentClient.id, updatedClient_2);
                  setIsEditUrl(false);
                })();
              }} scheme="light" variant="filled">{t("buttons.save")}</Button><Button onClick={() => {
                reset({
                  name: currentClient.name,
                  url: currentClient.url,
                  currency: currentClient.currency
                });
                setIsEditUrl(false);
                clearErrors("url");
              }} scheme="light" variant="tonal">{t("buttons.cancel")}</Button></EditInputsContainer></Grid> : <><Grid><Typography variant="body2">{t("placeholders.website")}</Typography><Typography variant="body3">{currentClient.url}</Typography></Grid><Button variant="text" onClick={() => {
              setIsEditUrl(true);
            }}>{t("buttons.change")}</Button></>}</StyledProfileItem><Divider /><InlineFlyoutAvatarUpload client={currentClient} title={t("titles.companyLogo")} uploadAvatarProps={uploadAvatarProps} /><TransparentDivider><Divider /></TransparentDivider><StyledTitle variant="h3">{t("titles.preferences")}</StyledTitle><StyledProfileItem>{isEditCurrency ? <Grid><Typography variant="h5">{t("titles.currency")}</Typography><SingleSelectDropdown title={t("titles.currencySelectTitle")} buttonWidth="100%" value={watchCurrency} onSelect={value => {
              setValue("currency", value, {
                shouldDirty: true
              });
            }} options={currencies.map(currency => ({
              label: `${currency.name} (${currency.code})`,
              value: currency.code,
              subCategory: frequentlyUsedCurrencies.includes(currency.code) ? t("descriptions.frequentlyUsedCurrencies") : t("descriptions.otherCurrencies")
            }))} /><Button onClick={() => {
              handleSubmit(async updatedClient_3 => {
                await handleUpdateClient(currentClient.id, updatedClient_3);
                setIsEditCurrency(false);
              })();
            }} scheme="light" variant="filled">{t("buttons.save")}</Button><Button onClick={() => {
              reset({
                name: currentClient.name,
                url: currentClient.url,
                currency: currentClient.currency
              });
              setIsEditCurrency(false);
              clearErrors("currency");
            }} scheme="light" variant="tonal">{t("buttons.cancel")}</Button></Grid> : <><Grid><Typography variant="body2">{t("titles.currency")}</Typography><Typography variant="body3">{currentClient.currency}</Typography></Grid><Button variant="text" onClick={() => {
              setIsEditCurrency(true);
            }}>{t("buttons.change")}</Button></>}</StyledProfileItem><TransparentDivider><Divider /></TransparentDivider><StyledTitle variant="h3">{t("titles.deleteWorkspace")}</StyledTitle><StyledDescription variant="body1">{t("descriptions.deleteWorkspace")}</StyledDescription><Button onClick={() => {
          setOpenDeletePopup(true);
        }} scheme="light" variant="outlined">{t("buttons.deleteWorkspace")}</Button><DeleteWorkspacePopup open={openDeletePopup} onClose={() => {
          setOpenDeletePopup(false);
        }} /></>}</Grid>;
    $[14] = clearErrors;
    $[15] = control;
    $[16] = currentClient;
    $[17] = handleSubmit;
    $[18] = handleUpdateClient;
    $[19] = isEditCurrency;
    $[20] = isEditName;
    $[21] = isEditUrl;
    $[22] = isUpdateClientLoading;
    $[23] = openDeletePopup;
    $[24] = reset;
    $[25] = setValue;
    $[26] = t;
    $[27] = uploadAvatarProps;
    $[28] = watchCurrency;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  return t6;
};
export default WorkspaceProfile;
const StyledTitle = styled(Typography)(({
  theme
}) => ({
  margin: '10px 0'
}));
const StyledDescription = styled(Typography)(({
  theme
}) => ({
  marginBottom: '25px'
}));
const TransparentDivider = styled(Grid)(({
  theme
}) => ({
  hr: {
    borderColor: 'transparent'
  }
}));
const EditInputsContainer = styled(Grid)(({
  theme
}) => ({
  form: {
    '>div': {
      width: '100%',
      paddingTop: '10px'
    }
  },
  button: {
    marginTop: '16px',
    '&:first-of-type': {
      marginRight: theme.spacing(1)
    }
  }
}));